import { keyBy } from '../../../../components/utils/utils'

export default function (campaign, config) {
  const {
    alwaysOn,
    brands,
    ccdbReportEnabled,
    ccdbReportType,
    channelPriority,
    contentClassification,
    contributionType,
    description,
    disableOrchestration,
    endDate,
    finality,
    manager,
    marketCodes,
    masterKey,
    name,
    onlyHeader,
    preview,
    recordType,
    segmentType,
    startDate,
    status,
    touchpoint,
    type
  } = campaign

  return {
    alwaysOn,
    brands,
    ccdbReportEnabled,
    ccdbReportType,
    channelPriority: channelPriority || null,
    contentClassification: contentClassification || null,
    contributionType: contributionType || null,
    description,
    disableOrchestration,
    endDate: endDate || null,
    finality,
    manager,
    marketCodes,
    masterKey,
    name,
    onlyHeader,
    preview,
    recordType,
    segmentType,
    startDate: startDate || null,
    status,
    touchpoint,
    type,
    config: _buildConfig(campaign, config)
  }
}

function _buildConfig (campaign, config) {
  const configObject = {}
  if (campaign.config.campaignData) {
    configObject.campaignData = campaign.config.campaignData
  }

  if (campaign.config.contents) {
    configObject.contents = campaign.config.contents || []
  }

  if (campaign.config.offers) {
    configObject.offers = _buildOffers(campaign.config.offers)
  }

  if (campaign.config.filters) {
    configObject.filters = _buildFilters(campaign, config, configObject.offers)
  }

  if (campaign.config.preview) {
    configObject.preview = campaign.config.preview
  }
  if (campaign.config.userPreferences) {
    configObject.userPreferences = campaign.config.userPreferences
  }

  return configObject
}

function _buildOffers (offers) {
  if (offers && offers[0] && offers[0].sectionId) {
    const keyed = keyBy(offers, 'offerId')
    const mapped = []

    Object.keys(keyed).forEach(offer => {
      const obj = { [offer]: [] }
      const sections = keyed[offer]

      sections.forEach(section => {
        obj[section.offerId].push({
          brand: section.brand,
          codMarket: section.codMarket,
          sectionId: section.sectionId,
          sectionsList: section.groupList.map(g => g.sectionId),
          contentPriority: section.contentPriority,
          tags: section.tags
        })
      })

      mapped.push(obj)
    })

    return mapped
  } else {
    return offers
  }
}

function _buildFilters (campaign, config, mappedOffers) {
  let brands = {}
  let markets = {}

  const isInformativeCampaign = campaign.config.userPreferences.onlyInformativeCampaign === null || campaign.config.userPreferences.onlyInformativeCampaign === true
  const isCommercialCampaignWithoutOffers = campaign.config.userPreferences.onlyInformativeCampaign === false && campaign.config.offers.length === 0
  if (isInformativeCampaign || isCommercialCampaignWithoutOffers) {
    brands = campaign.config.campaignData.campaignBrands
    markets = campaign.config.campaignData.markets.selected

    return _mapSingleFiltersSet(campaign.config.filters, config.filters, markets, brands)
  } else {
    let filters = {}
    let offers = mappedOffers.map(offer => Object.values(offer)).flat(2)

    Object.keys(campaign.config.filters).forEach(sectionId => {
      const offer = offers.find(o => o.sectionId === sectionId)
      const filter = campaign.config.filters[sectionId]

      if (offer.brand === 'All') {
        brands = {
          options: campaign.config.campaignData.campaignBrands.options,
          selected: campaign.config.campaignData.campaignBrands.selected
        }
      } else {
        brands = {
          options: campaign.config.campaignData.campaignBrands.options,
          selected: [offer.brand]
        }
      }
      markets = [offer.codMarket]
      filters[sectionId] = _mapSingleFiltersSet(filter, config.filters[sectionId] !== undefined ? config.filters[sectionId] : config.filters, markets, brands)
    })

    return filters
  }
}

function _mapSingleFiltersSet (filters, configFilters, markets, brands) {
  const filterSet = {
    brands: {
      options: brands.options,
      selected: brands.selected
    },
    carAges: filters.carAges,
    clientType: {
      options: configFilters.clientType.options,
      selected: (filters.clientType && filters.clientType.selected !== undefined) ? filters.clientType.selected : filters.clientType
    },
    communicationChannelsFilterLogic: {
      options: configFilters.communicationChannelsFilterLogic.options,
      selected: (filters.communicationChannelsFilterLogic && filters.communicationChannelsFilterLogic.selected !== undefined) ? filters.communicationChannelsFilterLogic.selected : filters.communicationChannelsFilterLogic
    },
    dealerCluster: {
      options: configFilters.dealerCluster.options,
      selected: (filters.dealerCluster && filters.dealerCluster.selected !== undefined) ? filters.dealerCluster.selected : filters.dealerCluster
    },
    dealers: {
      selected: (filters.dealers && filters.dealers.selected !== undefined) ? filters.dealers.selected : filters.dealers
    },
    emailEngagement: {
      options: configFilters.emailEngagement.options,
      selected: (filters.emailEngagement && filters.emailEngagement.selected !== undefined) ? filters.emailEngagement.selected : filters.emailEngagement
    },
    emailPressure: {
      options: configFilters.emailPressure.options,
      selected: (filters.emailPressure && filters.emailPressure.selected !== undefined) ? filters.emailPressure.selected : filters.emailPressure
    },
    emailReactivity: {
      options: configFilters.emailReactivity.options,
      selected: (filters.emailReactivity && filters.emailReactivity.selected !== undefined) ? filters.emailReactivity.selected : filters.emailReactivity
    },
    engagementConditions: {
      selected: (filters.engagementConditions && filters.engagementConditions.selected !== undefined) ? filters.engagementConditions.selected.filter(row => Object.values(row).some(i => !!i)) : filters.engagementConditions.filter(row => Object.values(row).some(i => !!i))
    },
    engineSize: {
      options: configFilters.engineSize.options,
      selected: (filters.engineSize && filters.engineSize.selected !== undefined) ? filters.engineSize.selected : filters.engineSize
    },
    excludeContactsWithoutAssociatedDealer: {
      options: configFilters.excludeContactsWithoutAssociatedDealer.options,
      selected: (filters.excludeContactsWithoutAssociatedDealer && filters.excludeContactsWithoutAssociatedDealer.selected !== undefined) ? filters.excludeContactsWithoutAssociatedDealer.selected : filters.excludeContactsWithoutAssociatedDealer
    },
    firstRegistrationDate: {
      selected: (filters.firstRegistrationDate && filters.firstRegistrationDate.selected !== undefined) ? filters.firstRegistrationDate.selected : filters.firstRegistrationDate
    },
    fleet: {
      options: configFilters.fleet.options,
      selected: (filters.fleet && filters.fleet.selected !== undefined) ? filters.fleet.selected : filters.fleet
    },
    fuelTypes: {
      options: configFilters.fuelTypes.options,
      selected: (filters.fuelTypes && filters.fuelTypes.selected !== undefined) ? filters.fuelTypes.selected : filters.fuelTypes
    },
    maintenanceModelCluster: {
      options: filters.maintenanceModelCluster.options,
      selected: (filters.maintenanceModelCluster && filters.maintenanceModelCluster.selected !== undefined) ? filters.maintenanceModelCluster.selected : filters.maintenanceModelCluster
    },
    markets: {
      selected: markets
    },
    marketingPrivacyConsent: {
      options: configFilters.marketingPrivacyConsent.options,
      selected: (filters.marketingPrivacyConsent && filters.marketingPrivacyConsent.selected !== undefined) ? filters.marketingPrivacyConsent.selected : filters.marketingPrivacyConsent
    },
    mileageTag: {
      options: configFilters.mileageTag.options,
      selected: (filters.mileageTag && filters.mileageTag.selected !== undefined) ? filters.mileageTag.selected : filters.mileageTag
    },
    mileages: {
      selected: (filters.mileages && filters.mileages.selected !== undefined) ? filters.mileages.selected : filters.mileages
    },
    modelYearFrom: {
      options: configFilters.modelYearFrom.options,
      selected: (filters.modelYearFrom && filters.modelYearFrom.selected !== undefined) ? filters.modelYearFrom.selected : filters.modelYearFrom
    },
    monthsFromFirstRegistrationDate: {
      selected: (filters.monthsFromFirstRegistrationDate && filters.monthsFromFirstRegistrationDate.selected !== undefined) ? filters.monthsFromFirstRegistrationDate.selected : filters.monthsFromFirstRegistrationDate
    },
    mvs: {
      selected: (filters.mvs && filters.mvs.selected !== undefined) ? filters.mvs.selected : filters.mvs
    },
    mvsToExclude: {
      selected: (filters.mvsToExclude && filters.mvsToExclude.selected !== undefined) ? filters.mvsToExclude.selected : filters.mvsToExclude
    },
    ocfDate: {
      selected: (filters.ocfDate && filters.ocfDate.selected !== undefined) ? filters.ocfDate.selected : filters.ocfDate
    },
    profilingPrivacyConsent: {
      options: configFilters.profilingPrivacyConsent.options,
      selected: (filters.profilingPrivacyConsent && filters.profilingPrivacyConsent.selected !== undefined) ? filters.profilingPrivacyConsent.selected : filters.profilingPrivacyConsent
    },
    purchasedServicesAndAccessories: {
      options: configFilters.purchasedServicesAndAccessories.options,
      selected: (filters.purchasedServicesAndAccessories && filters.purchasedServicesAndAccessories.selected !== undefined) ? filters.purchasedServicesAndAccessories.selected : filters.purchasedServicesAndAccessories
    },
    registrationOnMOC: {
      options: configFilters.registrationOnMOC.options,
      selected: (filters.registrationOnMOC && filters.registrationOnMOC.selected !== undefined) ? filters.registrationOnMOC.selected : filters.registrationOnMOC
    },
    sectionTitle: filters.sectionTitle,
    segment: {
      options: configFilters.segment.options,
      selected: (filters.segment && filters.segment.selected !== undefined) ? filters.segment.selected : filters.segment
    },
    targetCluster: {
      options: configFilters.targetCluster.options,
      selected: (filters.targetCluster && filters.targetCluster.selected !== undefined) ? filters.targetCluster.selected : filters.targetCluster
    },
    targetRange: {
      options: configFilters.targetRange.options,
      selected: (filters.targetRange && filters.targetRange.selected !== undefined) ? filters.targetRange.selected : filters.targetRange
    },
    vin: {
      selected: (filters.vin && filters.vin.selected !== undefined) ? filters.vin.selected : null
    },
    vinToExclude: {
      selected: (filters.vinToExclude && filters.vinToExclude.selected !== undefined) ? filters.vinToExclude.selected : filters.vinToExclude
    },
    warrantyExtension: {
      carsWithoutWarranties: {
        options: configFilters.warrantyExtension.carsWithoutWarranties.options,
        selected: (filters.warrantyExtension.carsWithoutWarranties !== undefined && filters.warrantyExtension.carsWithoutWarranties.selected !== undefined) ? filters.warrantyExtension.carsWithoutWarranties.selected : filters.warrantyExtension.carsWithoutWarranties
      },
      contracts: {
        options: configFilters.warrantyExtension.contracts.options,
        selected: (filters.warrantyExtension.contracts !== undefined && filters.warrantyExtension.contracts.selected !== undefined) ? filters.warrantyExtension.contracts.selected : filters.warrantyExtension.contracts
      },
      fromDate: filters.fromDate !== undefined ? filters.fromDate : null,
      toDate: filters.toDate !== undefined ? filters.toDate : null
    },
    warrantyExtensionInclude: {
      options: configFilters.warrantyExtensionInclude.options,
      selected: (filters.warrantyExtensionInclude && filters.warrantyExtensionInclude.selected !== undefined) ? filters.warrantyExtensionInclude.selected : filters.warrantyExtensionInclude
    },
    zipCodes: {
      selected: (filters.zipCodes && filters.zipCodes.selected !== undefined) ? filters.zipCodes.selected : filters.zipCodes
    }
  }

  return filterSet
}
